/**
 * The app_logs line for a Google calendar connect that ended without an authorization code.
 *
 * Pure and import-free so it can be unit-tested: the caller it serves is a React hook that reaches the
 * app's whole dependency graph, which this package's jest config cannot load (see orion-web/CLAUDE.md).
 *
 * WHY a closed window is now reported at all. Google Identity Services reports `popup_closed` when the
 * user dismisses the consent window — and ALSO when Google itself refused the consent, showed "This app
 * is blocked" inside that window, and the user closed the dead end. The two are the same string, so the
 * exclusion that kept dismissals out of the logs was also keeping out every blocked connect, which is
 * exactly the case worth seeing. It hid a real one for three weeks, until a user said so.
 *
 * So these lines do not claim to tell the two apart, because nothing available in the browser can. They
 * give the denominator instead: a share of attempts ending with no code, countable against the successes
 * the server already logs. A block moves that share, and no single event has to be classified for the
 * share to move.
 *
 * `afterMs` is the one extra piece of evidence there is. Dismissing means reading a permission list and
 * deciding against it; a block is a wall with nothing on it to read. Neither bound is sharp enough to
 * decide a single case, and it is deliberately reported rather than thresholded here — the query picks
 * the bound, so a wrong guess costs a dashboard edit rather than another three weeks of silence.
 */
export function googleConnectReport(ending: { message: string; afterMs: number; clientId: string }): string | null {
  const { message, afterMs, clientId } = ending;
  if (!message) {
    return null;
  }
  // `refused:` is kept verbatim for every other ending, because log searches and the calendar-connect
  // alert already key on that word.
  return message === CLOSED_WITH_NO_CODE
    ? `calendar-connect-google closed with no code after ${afterMs}ms — dismissed or blocked, Google reports both as this; client=${clientId}`
    : `calendar-connect-google refused: ${message} after ${afterMs}ms client=${clientId}`;
}

/** Google's own spelling, for the window closing before it produced a code. */
export const CLOSED_WITH_NO_CODE = 'popup_closed';
